import { appConfirm } from '../../../services/appDialogs';
import { AppSelect } from '../../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  Coins,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import {
  adjustUserPoints,
  createUserPointPlan,
  deleteUserPointPlan,
  fetchPointAccounts,
  fetchPointUserDetail,
  grantUserPoints,
  updateUserPointPlan,
  updateUserPointDailyWarningThreshold,
} from "../../../services/adminService";
import { displayUserId } from "../adminUtils";
import {
  ADMIN_ACCENT,
  EmptyText,
  ErrorNotice,
  FilterInput,
  IconAction,
  Panel,
  PrimaryButton,
  SecondaryButton,
  StatCard,
  StatusBadge,
} from "../adminUi.jsx";

const newGrantForm = () => ({ amount: "", note: "", requestId: crypto.randomUUID() });
const newAdjustForm = () => ({ delta: "", note: "", requestId: crypto.randomUUID() });
const EMPTY_PLAN = {
  id: null,
  cadence: "MONTHLY",
  amount: "",
  enabled: true,
  grantImmediately: false,
  note: "",
};

const CADENCE_LABELS = {
  DAILY: "每日（永久累加）",
  MONTHLY: "每月",
  YEARLY: "每年",
};

const LEDGER_LABELS = {
  GENERATION_CHARGE: "生成扣除",
  INITIAL_GRANT: "初始积分",
  ONE_TIME_GRANT: "一次性发放",
  ADMIN_ADJUSTMENT: "余额校正",
  RECURRING_GRANT: "周期发放",
  DAILY_QUOTA_RESET: "每日额度重置",
  POINT_BUCKET_MIGRATION: "积分账户分桶",
};

const formatPoints = (value) => Math.round(Number(value || 0)).toLocaleString("zh-CN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatOptionalPoints = (value) => value == null ? "-" : formatPoints(value);

const formatSignedPoints = (value) => {
  const number = Number(value || 0);
  return `${number > 0 ? "+" : ""}${formatPoints(number)}`;
};

const formatCurrency = (value) => Number(value || 0).toLocaleString("zh-CN", {
  style: "currency",
  currency: "CNY",
  minimumFractionDigits: 2,
});

const formatTime = (value) => {
  if (!value) return "-";
  const date = new Date(Number(value));
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const formatQuotaDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "-";
  return `${match[1]}年${Number(match[2])}月${Number(match[3])}日`;
};

const warningMeta = (row) => {
  if (row?.warningType === "negative") {
    return { label: "余额为负", color: "var(--color-error)" };
  }
  if (row?.warningType === "excessive") {
    return { label: "今日超量", color: "var(--color-warning)" };
  }
  return null;
};

const PointsTab = () => {
  const [summary, setSummary] = useState({
    totalBalance: 0,
    todayConsumed: 0,
    userCount: 0,
    warningCount: 0,
    negativeCount: 0,
    excessiveCount: 0,
    pointsPerCny: 100,
    items: [],
  });
  const [filters, setFilters] = useState({ keyword: "", onlyWarning: false, limit: 1000 });
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [grantForm, setGrantForm] = useState(newGrantForm);
  const [adjustForm, setAdjustForm] = useState(newAdjustForm);
  const [warningThreshold, setWarningThreshold] = useState("");
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPointAccounts(filters);
      setSummary(data || {});
      setLoadedOnce(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || "积分账户加载失败");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadDetail = useCallback(async (userId) => {
    if (!userId) return;
    setDetailLoading(true);
    setError("");
    try {
      const data = await fetchPointUserDetail(userId, 300);
      setDetail(data || {});
      setWarningThreshold(String(
        data?.account?.dailyWarningThreshold
        ?? data?.account?.warningThreshold
        ?? 60000
      ));
    } catch (e) {
      console.error(e);
      setError(e?.message || "积分详情加载失败");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const openUser = (userId) => {
    setSelectedUser(userId);
    setDetail(null);
    setGrantForm(newGrantForm());
    setAdjustForm(newAdjustForm());
    setPlanForm(EMPTY_PLAN);
    loadDetail(userId);
  };

  const closeDetail = () => {
    setSelectedUser("");
    setDetail(null);
    setMessage("");
  };

  const runAction = async (action, successText) => {
    setActionLoading(true);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(successText);
      await Promise.all([loadAccounts(), loadDetail(selectedUser)]);
      return true;
    } catch (e) {
      console.error(e);
      setError(e?.message || "积分操作失败");
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const submitGrant = async () => {
    if (!grantForm.amount || Number(grantForm.amount) <= 0) {
      setError("一次性发放的永久积分必须大于 0");
      return;
    }
    const ok = await runAction(
      () => grantUserPoints(selectedUser, grantForm),
      `已向 ${displayUserId(selectedUser)} 发放 ${formatPoints(grantForm.amount)} 永久积分`
    );
    if (ok) setGrantForm(newGrantForm());
  };

  const submitAdjust = async () => {
    if (!adjustForm.delta || Number(adjustForm.delta) === 0) {
      setError("校正值不能为 0，正数增加、负数扣减");
      return;
    }
    const ok = await runAction(
      () => adjustUserPoints(selectedUser, adjustForm),
      `已校正 ${displayUserId(selectedUser)} 的永久积分`
    );
    if (ok) setAdjustForm(newAdjustForm());
  };

  const submitThreshold = () => {
    if (warningThreshold === "" || Number(warningThreshold) < 0) {
      setError("每日消耗阈值不能小于 0");
      return;
    }
    runAction(
      () => updateUserPointDailyWarningThreshold(selectedUser, warningThreshold),
      "每日消耗阈值已更新"
    );
  };

  const submitPlan = async () => {
    if (!planForm.amount || Number(planForm.amount) <= 0) {
      setError("周期发放的永久积分必须大于 0");
      return;
    }
    const body = {
      cadence: planForm.cadence,
      amount: planForm.amount,
      enabled: planForm.enabled,
      grantImmediately: planForm.grantImmediately,
      note: planForm.note,
    };
    const editing = Boolean(planForm.id);
    const ok = await runAction(
      () => editing
        ? updateUserPointPlan(planForm.id, body)
        : createUserPointPlan(selectedUser, body),
      editing ? "永久积分周期计划已更新" : "永久积分周期计划已创建"
    );
    if (ok) setPlanForm(EMPTY_PLAN);
  };

  const editPlan = (plan) => {
    setPlanForm({
      id: plan.id,
      cadence: plan.cadence,
      amount: String(plan.amount ?? ""),
      enabled: plan.enabled !== false,
      grantImmediately: false,
      note: plan.note || "",
    });
  };

  const togglePlan = (plan) => {
    runAction(
      () => updateUserPointPlan(plan.id, { enabled: !plan.enabled }),
      plan.enabled ? "周期计划已暂停" : "周期计划已启用"
    );
  };

  const removePlan = async (plan) => {
    if (!(await appConfirm(`确认删除「${CADENCE_LABELS[plan.cadence] || plan.cadence} ${formatPoints(plan.amount)} 积分」计划？`))) {
      return;
    }
    runAction(() => deleteUserPointPlan(plan.id), "周期计划已删除");
  };

  const rows = useMemo(() => summary.items || [], [summary.items]);
  const hasBalanceBreakdown = rows.some(
    (row) => row?.dailyRemaining != null || row?.permanentBalance != null
  );
  const totalDailyRemaining = summary.totalDailyRemaining ?? (hasBalanceBreakdown
    ? rows.reduce((total, row) => total + Number(row?.dailyRemaining || 0), 0)
    : null);
  const totalPermanentBalance = summary.totalPermanentBalance ?? (hasBalanceBreakdown
    ? rows.reduce((total, row) => total + Number(row?.permanentBalance || 0), 0)
    : null);
  const account = detail?.account;
  const plans = detail?.plans || [];
  const ledger = detail?.ledger || [];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-4">
        <StatCard label="总可用积分" value={formatPoints(summary.totalBalance)} color={ADMIN_ACCENT} />
        <StatCard label="今日额度剩余" value={formatOptionalPoints(totalDailyRemaining)} />
        <StatCard label="永久积分合计" value={formatOptionalPoints(totalPermanentBalance)} />
        <StatCard label="账户人数" value={summary.userCount || 0} />
        <StatCard label="告警账户" value={summary.warningCount || 0} color={summary.warningCount ? "var(--color-warning)" : undefined} />
        <StatCard label="负余额" value={summary.negativeCount || 0} color={summary.negativeCount ? "var(--color-error)" : undefined} />
        <StatCard label="今日消耗" value={formatPoints(summary.todayConsumed)} color={summary.excessiveCount ? "var(--color-warning)" : undefined} />
      </div>

      {error && <ErrorNotice message={error} />}
      {message && (
        <div
          className="rounded-lg px-3.5 py-2.5 text-sm mb-4 animate-in fade-in slide-in-from-top-1 duration-200"
          style={{ backgroundColor: "rgba(34,197,94,0.12)", border: "1px solid var(--color-success)", color: "var(--color-success)" }}
        >
          {message}
        </div>
      )}

      <Panel
        title="积分账户"
        sub="生成费用完成结算后按 100 积分 = 1 元扣除，先用今日额度，再用永久积分"
        right={(
          <button
            type="button"
            onClick={loadAccounts}
            disabled={loading}
            title="刷新积分账户"
            className="w-8 h-8 inline-flex items-center justify-center rounded-md border cursor-pointer disabled:opacity-50"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        )}
      >
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <FilterInput
            label="用户搜索"
            value={filters.keyword}
            placeholder="用户名"
            onChange={(value) => setFilters(current => ({ ...current, keyword: value }))}
          />
          <label className="h-8 inline-flex items-center gap-2 px-3 rounded-md border text-xs cursor-pointer"
            style={{ backgroundColor: "var(--color-bg-tertiary)", borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
            <input
              type="checkbox"
              checked={filters.onlyWarning}
              onChange={(event) => setFilters(current => ({ ...current, onlyWarning: event.target.checked }))}
            />
            只看告警
          </label>
        </div>

        {!loadedOnce && loading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map(index => <div key={index} className="skeleton h-9" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-label text-left">
                  <th className="font-medium pb-2.5 pr-4">用户</th>
                  <th className="font-medium pb-2.5 pr-4">状态</th>
                  <th className="font-medium pb-2.5 pr-4">总可用积分</th>
                  <th className="font-medium pb-2.5 pr-4">今日剩余</th>
                  <th className="font-medium pb-2.5 pr-4">永久积分</th>
                  <th className="font-medium pb-2.5 pr-4">今日消耗 / 阈值</th>
                  <th className="font-medium pb-2.5 pr-4">累计发放</th>
                  <th className="font-medium pb-2.5 pr-4">累计消耗</th>
                  <th className="font-medium pb-2.5 pr-4">最近变动</th>
                  <th className="font-medium pb-2.5">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={10}><EmptyText text="暂无积分账户" /></td></tr>
                ) : rows.map(row => {
                  const warning = warningMeta(row);
                  return (
                    <tr
                      key={row.userId}
                      onClick={() => openUser(row.userId)}
                      className="hover:bg-[var(--color-bg-tertiary)] transition-colors cursor-pointer"
                      style={{ borderTop: "1px solid var(--color-border-secondary)" }}
                    >
                      <td className="py-2.5 pr-4 text-xs font-medium">{displayUserId(row.userId)}</td>
                      <td className="py-2.5 pr-4">
                        {warning
                          ? <StatusBadge label={warning.label} color={warning.color} />
                          : <StatusBadge label="正常" color="var(--color-success)" />}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-semibold font-mono-num" style={{ color: warning?.color || "var(--color-text-primary)" }}>
                        {formatPoints(row.balance)}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-semibold font-mono-num" style={{ color: ADMIN_ACCENT }}>
                        {formatOptionalPoints(row.dailyRemaining)}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-mono-num">
                        {formatOptionalPoints(row.permanentBalance)}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-mono-num whitespace-nowrap" style={{ color: row.excessiveWarning ? "var(--color-warning)" : "var(--color-text-muted)" }}>
                        {formatPoints(row.todayConsumed)} / {formatPoints(row.dailyWarningThreshold)}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-mono-num">{formatPoints(row.totalGranted)}</td>
                      <td className="py-2.5 pr-4 text-xs font-mono-num">{formatPoints(row.totalConsumed)}</td>
                      <td className="py-2.5 pr-4 text-2xs font-mono-num whitespace-nowrap" style={{ color: "var(--color-text-muted)" }}>
                        {formatTime(row.updatedAt)}
                      </td>
                      <td className="py-2.5" onClick={(event) => event.stopPropagation()}>
                        <SecondaryButton onClick={() => openUser(row.userId)}>管理</SecondaryButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {selectedUser && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 animate-in fade-in duration-150"
          style={{ backgroundColor: "rgba(0,0,0,0.65)", zIndex: "var(--z-modal)" }}
          onClick={closeDetail}
        >
          <div
            className="w-full max-w-5xl max-h-[90vh] rounded-xl border overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-secondary)", boxShadow: "var(--shadow-modal)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="h-16 px-5 flex items-center justify-between border-b" style={{ borderColor: "var(--color-border-secondary)" }}>
              <div className="min-w-0">
                <div className="text-label">积分管理</div>
                <div className="text-lg font-semibold truncate mt-0.5">{displayUserId(selectedUser)}</div>
              </div>
              <IconAction title="关闭" onClick={closeDetail} icon={X} />
            </header>

            <div className="p-5 overflow-y-auto custom-scrollbar" style={{ maxHeight: "calc(90vh - 64px)" }}>
              {detailLoading && !detail ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    {[0, 1, 2].map(index => <div key={index} className="skeleton h-20" />)}
                  </div>
                  <div className="skeleton h-48" />
                  <div className="skeleton h-48" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-5">
                    <StatCard label="总可用积分" value={formatPoints(account?.balance)} color={warningMeta(account)?.color || ADMIN_ACCENT} />
                    <StatCard label="今日额度剩余" value={formatOptionalPoints(account?.dailyRemaining)} color={ADMIN_ACCENT} />
                    <StatCard label="永久积分" value={formatOptionalPoints(account?.permanentBalance)} />
                    <StatCard label="每日额度" value={formatOptionalPoints(account?.dailyQuota)} />
                    <StatCard label="额度日期" value={formatQuotaDate(account?.dailyQuotaDate)} />
                    <StatCard label="今日消耗" value={formatPoints(account?.todayConsumed)} color={account?.excessiveWarning ? "var(--color-warning)" : undefined} />
                    <StatCard label="每日阈值" value={formatPoints(account?.dailyWarningThreshold)} />
                    <StatCard label="累计消耗" value={formatPoints(account?.totalConsumed)} />
                  </div>

                  <div className="grid lg:grid-cols-2 gap-5 mb-5">
                    <section className="border-t pt-4" style={{ borderColor: "var(--color-border-secondary)" }}>
                      <div className="flex items-center gap-2 mb-3">
                        <Plus size={15} style={{ color: ADMIN_ACCENT }} />
                        <h4 className="text-sm font-semibold">一次性发放永久积分</h4>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <FilterInput label="积分数量" type="number" min="1" step="1" value={grantForm.amount} onChange={(value) => setGrantForm(current => ({ ...current, amount: value }))} />
                        <FilterInput label="备注" value={grantForm.note} onChange={(value) => setGrantForm(current => ({ ...current, note: value }))} />
                      </div>
                      <PrimaryButton onClick={submitGrant} disabled={actionLoading} className="mt-3">
                        发放永久积分
                      </PrimaryButton>
                    </section>

                    <section className="border-t pt-4" style={{ borderColor: "var(--color-border-secondary)" }}>
                      <div className="flex items-center gap-2 mb-3">
                        <Pencil size={15} style={{ color: "var(--color-blue-primary)" }} />
                        <h4 className="text-sm font-semibold">永久积分校正</h4>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <FilterInput label="校正值" type="number" step="1" value={adjustForm.delta} onChange={(value) => setAdjustForm(current => ({ ...current, delta: value }))} />
                        <FilterInput label="备注" value={adjustForm.note} onChange={(value) => setAdjustForm(current => ({ ...current, note: value }))} />
                      </div>
                      <SecondaryButton onClick={submitAdjust} disabled={actionLoading} className="mt-3">
                        应用校正
                      </SecondaryButton>
                      <div className="text-2xs mt-2" style={{ color: "var(--color-text-muted)" }}>只调整永久积分；正数增加，负数扣减，不修改今日免费额度。</div>
                    </section>
                  </div>

                  <section className="border-t pt-4 mb-5" style={{ borderColor: "var(--color-border-secondary)" }}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <CalendarClock size={15} style={{ color: ADMIN_ACCENT }} />
                          <h4 className="text-sm font-semibold">永久积分自动发放计划</h4>
                        </div>
                        <div className="text-2xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                          每日免费额度由系统自动重置，不在这里配置；以下计划发放的是额外永久积分，会持续累加。
                        </div>
                      </div>
                      {planForm.id && (
                        <SecondaryButton onClick={() => setPlanForm(EMPTY_PLAN)}>
                          取消编辑
                        </SecondaryButton>
                      )}
                    </div>

                    <div className="flex flex-wrap items-end gap-3 mb-4">
                      <div>
                        <label className="text-label block mb-1.5">周期</label>
                        <AppSelect
                          value={planForm.cadence}
                          onChange={(event) => setPlanForm(current => ({ ...current, cadence: event.target.value }))}
                          className="h-8 px-2.5 rounded-md text-xs outline-none"
                          style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
                        >
                          <option value="DAILY">每日（永久累加）</option>
                          <option value="MONTHLY">每月</option>
                          <option value="YEARLY">每年</option>
                        </AppSelect>
                      </div>
                      <FilterInput label="每次发放永久积分" type="number" min="1" step="1" value={planForm.amount} onChange={(value) => setPlanForm(current => ({ ...current, amount: value }))} />
                      <FilterInput label="备注" value={planForm.note} onChange={(value) => setPlanForm(current => ({ ...current, note: value }))} />
                      {!planForm.id && (
                        <label className="h-8 inline-flex items-center gap-2 px-3 rounded-md border text-xs cursor-pointer"
                          style={{ backgroundColor: "var(--color-bg-tertiary)", borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
                          <input
                            type="checkbox"
                            checked={planForm.grantImmediately}
                            onChange={(event) => setPlanForm(current => ({ ...current, grantImmediately: event.target.checked }))}
                          />
                          创建后立即发一次
                        </label>
                      )}
                      <PrimaryButton onClick={submitPlan} disabled={actionLoading} className="inline-flex items-center gap-1.5">
                        {planForm.id ? <Check size={14} /> : <Plus size={14} />}
                        {planForm.id ? "保存计划" : "新建计划"}
                      </PrimaryButton>
                    </div>

                    <div className="overflow-x-auto rounded-lg border" style={{ borderColor: "var(--color-border-secondary)" }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-label text-left">
                            <th className="font-medium px-3 py-2">周期</th>
                            <th className="font-medium px-3 py-2">永久积分</th>
                            <th className="font-medium px-3 py-2">状态</th>
                            <th className="font-medium px-3 py-2">下次发放</th>
                            <th className="font-medium px-3 py-2">最近发放</th>
                            <th className="font-medium px-3 py-2">备注</th>
                            <th className="font-medium px-3 py-2">操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plans.length === 0 ? (
                            <tr><td colSpan={7}><EmptyText text="暂无永久积分自动发放计划" /></td></tr>
                          ) : plans.map(plan => (
                            <tr key={plan.id} style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
                              <td className="px-3 py-2 text-xs">{CADENCE_LABELS[plan.cadence] || plan.cadence}</td>
                              <td className="px-3 py-2 text-xs font-mono-num">{formatPoints(plan.amount)}</td>
                              <td className="px-3 py-2"><StatusBadge label={plan.enabled ? "启用" : "暂停"} color={plan.enabled ? "var(--color-success)" : "var(--color-text-muted)"} /></td>
                              <td className="px-3 py-2 text-2xs font-mono-num whitespace-nowrap">{formatTime(plan.nextGrantAt)}</td>
                              <td className="px-3 py-2 text-2xs font-mono-num whitespace-nowrap">{formatTime(plan.lastGrantAt)}</td>
                              <td className="px-3 py-2 text-xs max-w-48 truncate" title={plan.note || ""}>{plan.note || "-"}</td>
                              <td className="px-3 py-2">
                                <div className="flex gap-1">
                                  <IconAction title="编辑计划" onClick={() => editPlan(plan)} icon={Pencil} />
                                  <IconAction title={plan.enabled ? "暂停计划" : "启用计划"} onClick={() => togglePlan(plan)} icon={plan.enabled ? X : Check} />
                                  <IconAction title="删除计划" onClick={() => removePlan(plan)} icon={Trash2} danger />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section className="border-t pt-4 mb-5" style={{ borderColor: "var(--color-border-secondary)" }}>
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <AlertTriangle size={15} style={{ color: "var(--color-warning)" }} />
                          <h4 className="text-sm font-semibold">每日消耗告警</h4>
                        </div>
                        <div className="text-2xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                          当天生成消耗超过该用户自己的阈值时进入告警列表，次日按自然日重新计算。
                        </div>
                      </div>
                      <FilterInput label="每日消耗阈值" type="number" min="0" step="1" value={warningThreshold} onChange={setWarningThreshold} />
                      <SecondaryButton onClick={submitThreshold} disabled={actionLoading}>保存阈值</SecondaryButton>
                    </div>
                  </section>

                  <section className="border-t pt-4" style={{ borderColor: "var(--color-border-secondary)" }}>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Coins size={15} style={{ color: ADMIN_ACCENT }} />
                          <h4 className="text-sm font-semibold">积分流水</h4>
                        </div>
                        <div className="text-2xs mt-1" style={{ color: "var(--color-text-muted)" }}>记录发放、校正和每次生成扣除。</div>
                      </div>
                      <span className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>{ledger.length} 条</span>
                    </div>
                    <div className="overflow-x-auto rounded-lg border" style={{ borderColor: "var(--color-border-secondary)" }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-label text-left">
                            <th className="font-medium px-3 py-2">时间</th>
                            <th className="font-medium px-3 py-2">类型</th>
                            <th className="font-medium px-3 py-2">变动</th>
                            <th className="font-medium px-3 py-2">余额</th>
                            <th className="font-medium px-3 py-2">费用</th>
                            <th className="font-medium px-3 py-2">模型/来源</th>
                            <th className="font-medium px-3 py-2">备注</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ledger.length === 0 ? (
                            <tr><td colSpan={7}><EmptyText text="暂无积分流水" /></td></tr>
                          ) : ledger.map(item => (
                            <tr key={item.id} style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
                              <td className="px-3 py-2 text-2xs font-mono-num whitespace-nowrap">{formatTime(item.createdAt)}</td>
                              <td className="px-3 py-2 text-xs">{LEDGER_LABELS[item.type] || item.type}</td>
                              <td className="px-3 py-2 text-xs font-mono-num">
                                <div className="font-semibold" style={{ color: Number(item.delta) < 0 ? "var(--color-error)" : "var(--color-success)" }}>
                                  {formatSignedPoints(item.delta)}
                                </div>
                                {(item.dailyDelta != null || item.permanentDelta != null) && (
                                  <div className="mt-0.5 whitespace-nowrap text-[9px]" style={{ color: "var(--color-text-muted)" }}>
                                    今日 {formatSignedPoints(item.dailyDelta)} · 永久 {formatSignedPoints(item.permanentDelta)}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs font-mono-num">
                                <div>{formatPoints(item.balanceAfter)}</div>
                                {(item.dailyBalanceAfter != null || item.permanentBalanceAfter != null) && (
                                  <div className="mt-0.5 whitespace-nowrap text-[9px]" style={{ color: "var(--color-text-muted)" }}>
                                    今日 {formatPoints(item.dailyBalanceAfter)} · 永久 {formatPoints(item.permanentBalanceAfter)}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs font-mono-num">{item.costCny != null ? formatCurrency(item.costCny) : "-"}</td>
                              <td className="px-3 py-2 text-xs max-w-52 truncate" title={item.model || item.source || ""}>{item.model || item.source || "-"}</td>
                              <td className="px-3 py-2 text-xs max-w-64 truncate" title={item.note || ""}>{item.note || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsTab;
